/**
 * Utility functions for converting link IDs back to actual links in partner responses
 */

export interface LinkMapping {
  link_id: number;
  url: string;
  description?: string;
}

/**
 * Converts link IDs back to markdown links in text
 * Changes [Description](linkId) back to [Description](url)
 * @param text - Text containing link IDs
 * @param linkMappings - Array of link mappings from the database
 * @returns Text with link IDs replaced by actual URLs
 */
export function convertLinkIdsToLinks(text: string, linkMappings: LinkMapping[]): string {
  if (!text || !linkMappings || linkMappings.length === 0) {
    return text;
  }

  // Create a map for quick lookup
  const linkMap = new Map<number, LinkMapping>();
  linkMappings.forEach(link => {
    linkMap.set(link.link_id, link);
  });

  // Match [Description](linkId) where linkId is a number
  const linkIdRegex = /\[([^\]]*)\]\((\d+)\)/g;
  
  return text.replace(linkIdRegex, (match, description, linkId) => {
    const linkData = linkMap.get(parseInt(linkId));
    if (linkData) {
      return `[${description}](${linkData.url})`;
    }
    return match; // Return original if link not found
  });
}

/**
 * Fetches all link mappings from the API
 * @param apiBaseUrl - Base URL for the API
 * @returns Promise<LinkMapping[]> - Array of link mappings
 */
export async function fetchLinkMappings(apiBaseUrl: string): Promise<LinkMapping[]> {
  try {
    const response = await fetch(`${apiBaseUrl}/api/admin/link-mappings`);
    if (!response.ok) {
      throw new Error(`Failed to fetch link mappings: ${response.status}`);
    }
    const data = await response.json();
    return data || [];
  } catch (error) {
    console.error('Error fetching link mappings:', error);
    return [];
  }
}

/**
 * Processes a message content to convert link IDs to actual links
 * @param content - Message content (string or React node)
 * @param linkMappings - Array of link mappings
 * @returns Processed content with links converted
 */
export function processMessageContent(
  content: string | React.ReactNode, 
  linkMappings: LinkMapping[]
): string | React.ReactNode {
  // Only process string content
  if (typeof content !== 'string') {
    return content;
  }

  return convertLinkIdsToLinks(content, linkMappings);
}
